import { getNotifications, selectChatNotifications, selectChatNotificationsMeta } from '../chatSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import React, { useEffect, useRef, useState } from 'react'
import { selectUser } from 'containers/Login/loginSlice'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import { useHistory } from 'react-router-dom'
import SendPhotoPopup from './SendPhotoPopup'
import Notification from './Notification'
import Loading from 'components/Loading'
import BasicPopup from './BasicPopup'
import Error from 'components/Error'
import { toTime } from 'utils/date'

interface NotificationsProps {
  onlyImportantNotifications?: boolean
}

const Notifications: React.FC<NotificationsProps> = ({ onlyImportantNotifications }) => {
  const user = useReduxSelector(selectUser)
  const [isLoading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(false)
  const allNotifications = useReduxSelector(selectChatNotifications)
  const meta = useReduxSelector(selectChatNotificationsMeta)
  const notifications = onlyImportantNotifications
    ? allNotifications.filter(notification => notification.type === 'warning')
    : allNotifications
  const userHasUnpaidInvoices = Array.isArray(user?.unpaid_invoices) && user.unpaid_invoices.length > 0
  const selectedNotificationsLength = notifications.length + (userHasUnpaidInvoices ? 1 : 0)

  const listRef = useRef<HTMLDivElement | null>(null)
  const { popup } = usePopupState<chat.NotificationPopup | undefined>()

  const { push } = useHistory()
  const dispatch = useReduxDispatch()
  const { t, lang } = useTranslation()

  const handleScroll = () => {
    if (isLoading) return
    const list = listRef.current
    const pagination = meta?.pagination
    if (!list || !pagination) return
    const { scrollTop, scrollHeight, clientHeight } = list
    if (
      pagination
      && pagination.links.next
      && pagination.count > pagination.current_page
      && scrollHeight - scrollTop - 2 <= clientHeight
    ) {
      setLoading(true)
      dispatch(getNotifications(pagination.links.next)).then(() => {
        setLoading(false)
      })
    }
  }

  useEffect(() => {
    dispatch(getNotifications()).then(() => {
      setInitialLoading(true)
    })
  }, [dispatch])

  useEffect(() => {
    if (initialLoading) {
      handleScroll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, initialLoading])

  return (
    <>
      {popup?.type === 'notificationModal' && <BasicPopup
        {...popup.props}
      />}
      {popup?.type === 'sendPhoto' && <SendPhotoPopup
        {...popup.props}
      />}
      {!onlyImportantNotifications && <h4 className='--secFont'>{t('basic.messages')}</h4>}
      <div
        ref={listRef}
        className='chat__list'
        onScroll={handleScroll}>
        {selectedNotificationsLength > 0
          ? <>
            {userHasUnpaidInvoices && <div className='list__contact__wrapper'>
              <div
                className='list__contact group'
                onClick={() => {
                  push('/account/invoices?selectAll=1')
                }}>
                <div className='contact__avatar center'>
                  <i className='icon icon--logo s24' />
                </div>
                <div className='contact__desc'>
                  <div className='--secFont'>{t(user?.unpaid_invoices?.length === 1 ? 'chat.youHaveInvoiceToPay' : 'chat.youHaveInvoicesToPay')}</div>
                  <div className='chat__text'>{t('chat.pleasePayInvoices')} {user?.unpaid_invoices?.length === 1 ? t('chat.invoice') : `${user?.unpaid_invoices?.length} ${t('chat.invoices')}`}</div>
                </div>
                <div className='contact__time chat__text'>
                  {toTime(new Date(), lang)}
                </div>
              </div>
            </div>}
            {notifications.map((notification) => <Notification
              key={notification.id}
              isFullyLoaded={initialLoading}
              {...notification}
            />)}
          </>
          : <Error text={t('basic.noMessages')} />}
        {isLoading && <div className='list__footer'><Loading /></div>}
        {!isLoading
          && selectedNotificationsLength > 0
          && !onlyImportantNotifications
          && meta?.pagination?.current_page === meta?.pagination?.total_pages
          && <div className='list__footer'>
            <hr />
            <span className='chat__text'>{t('basic.thatIsAll')}</span>
          </div>}
      </div>
    </>
  )
}

export default Notifications